import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  TenantMemberDisplayData,
} from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useMemo, useRef } from 'react'

export type SearchResults = {
  tenantMembers: TenantMemberDisplayData[]
}

export type TenantMemberSearchFn = (params: { search?: string }) => Promise<SearchResults>

/**
 * Debounced federated search for autocomplete fields that accept teams and members.
 *
 * Same duplicate-prevention as {@link useSearchTenantMembers}: `selectedMembersIds`
 * are filtered out of both team and member result lists before they reach the UI.
 */
const useSearchTenantMembersAndTeams = (
  /** Current autocomplete input; query runs only when this is non-empty. */
  search: string,
  /** Recipient IDs already chosen in the form; excluded from dropdown options. */
  selectedMembersIds: string[] = [],
  /** Injected search API (e.g. `listRecognitionRecipients`). */
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>,
  /** When set, forwarded to `searchFn` for delegated recognition recipient search. */
  delegatorId?: string,
) => {
  const lastMatches = useRef<FederatedSearchRecipientsResults | undefined>(undefined)

  const {
    data: matches,
    isSuccess,
    isFetching,
  } = useQuery({
    queryKey: ['searchTenantMembersAndTeams', search, delegatorId],
    queryFn: async () => {
      const result = await searchFn({ search, delegatorId })
      return result
    },
    placeholderData: lastMatches.current,
    enabled: !isEmpty(search),
  })

  if (isSuccess) {
    lastMatches.current = matches
  }

  const searchedTenantMembersAndTeams = useMemo(() => {
    if (isEmpty(search) || !matches) {
      return []
    }

    const teamMatches =
      matches.teams?.filter(match => !selectedMembersIds.find(id => id === match.id)) || []
    const memberMatches =
      matches.tenantMembers?.filter(match => !selectedMembersIds.find(id => id === match.id)) || []
    const allMatches = [...teamMatches, ...memberMatches]
    return allMatches
  }, [matches, search, selectedMembersIds])

  return {
    searchedTenantMembersAndTeams,
    isFetching,
  }
}

export default useSearchTenantMembersAndTeams
